import { Card, CardActionArea, CardContent, CardMedia, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ConstructionIcon from "@mui/icons-material/Construction";
import DoDisturbOffIcon from "@mui/icons-material/DoDisturbOff";
import EngineeringIcon from "@mui/icons-material/Engineering";
import { CircularProgress } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { userInformationService, userPermitsService } from "src/App/api/Services";
import projectCardDefaultImg from "../../../../assets/images/bdt_projectCardDefault.jpg";
import { ProjectDto, ProjectStatusEnum } from "../../../api/APIClient";
import { BDTRoutes } from "../../../helpers/applicationConstants";
import { usePanelProjectContext } from "../../PanelProjectProvider";

const useStyles = makeStyles((theme) => ({
    root: {
        width: "25vw",
        maxWidth: "350px",
        minWidth: "250px",
        minHeight: "172.5px",
        height: "280px",
        marginRight: "5vw",
        marginBottom: "5vw",
    },
    media: {
        height: "50%",
        paddingTop: "56.25%",
    },
    mediaGray: {
        height: "50%",
        paddingTop: "56.25%",
        filter: "grayscale(1)",
    },
    noHover: {
        cursor: "not-allowed",
    },
    hover: {
        cursor: "pointer",
    },
    icon: {
        top: "35%",
        left: "50%",
        position: "absolute",
        transform: "translate(-50%, -50%)",
        color: "#000000",
        width: "120px !important",
        height: "120px !important",
        opacity: "0.8",
    },
    progress: {
        top: "35%",
        left: "50%",
        position: "absolute",
        transform: "translate(-50%, -50%)",
        color: "#000000",
        width: "120px !important",
        height: "120px !important",
        opacity: "0.8",
    },
}));

interface ProjectCardProps {
    project: ProjectDto;
}

const ProjectCard = ({ project }: ProjectCardProps): JSX.Element => {
    const classes = useStyles();
    const { t } = useTranslation("administration");
    const { userId, userPermits } = usePanelProjectContext();

    const isReady = project.status === ProjectStatusEnum.Ready;
    const isCreating = project.status === ProjectStatusEnum.Creating;

    const [isAuthByPermits, setIsAuthByPermits] = React.useState<boolean>(false);
    const [loadingAuthByPermits, setLoadingAuthByPermits] = React.useState<boolean>(false);

    const getUserPermitsByProjectId = () => {
        // @TODO: descomentar esto cuando funcione bien el endpoint
        // --------------------------------------------------------------------------------
        setLoadingAuthByPermits(true);
        userPermitsService
            .getUserPermits({ projectId: project.id })
            .then((response) => {
                const allValues = Object.values(response.data);
                if (allValues.some((vl) => vl > 0)) setIsAuthByPermits(true);
                else setIsAuthByPermits(false);
            })
            .catch((err) => {
                console.error(err);
                setIsAuthByPermits(false);
            })
            .finally(() => {
                setLoadingAuthByPermits(false);
            });
        // --------------------------------------------------------------------------------

        // setIsAuthByPermits(true);
    };

    const message = () => {
        if (!isAuthByPermits && !loadingAuthByPermits) {
            return `${t("projects.noPermitsToAccessThisProject")}`;
        }
        if (isCreating) {
            return `${t("projects.projectCreating")}`;
        }
        if (isReady) {
            return "";
        }

        return `${t("projects.projectUpdating")}`;
    };

    React.useEffect(() => {
        getUserPermitsByProjectId();
    }, [project, userPermits]);

    return (
        <Card className={classes.root} variant="outlined">
            <CardActionArea
                // className={isCreating ? classes.noHover : classes.hover}
                className={!isCreating && isAuthByPermits ? classes.hover : classes.noHover}
            >
                {isCreating && (
                    <>
                        <CardMedia
                            key="primerCardMedia"
                            className={classes.mediaGray}
                            image={
                                project.projectImageStr
                                    ? project.projectImageStr
                                    : projectCardDefaultImg
                            }
                        />
                        <ConstructionIcon className={classes.icon} />
                    </>
                )}

                {!isCreating && !isAuthByPermits && (
                    <>
                        <CardMedia
                            key="segundoCardMedia"
                            className={classes.mediaGray}
                            image={
                                project.projectImageStr
                                    ? project.projectImageStr
                                    : projectCardDefaultImg
                            }
                        />
                        {loadingAuthByPermits ? (
                            <CircularProgress
                                style={{
                                    top: "15%",
                                    left: "33%",
                                    position: "absolute",
                                    transform: "translate(-50%, -50%)",
                                    color: "#000000",
                                    width: "120px",
                                    height: "120px",
                                    opacity: "0.8",
                                }}
                            />
                        ) : (
                            <DoDisturbOffIcon className={classes.icon} />
                        )}
                    </>
                )}

                {!isCreating && isAuthByPermits && (
                    <Link to={BDTRoutes.Project.Viewer.Viewer.Builder(project.id)}>
                        <CardMedia
                            key="tercerCardMedia"
                            className={isReady ? classes.media : classes.mediaGray}
                            image={
                                project.projectImageStr
                                    ? project.projectImageStr
                                    : projectCardDefaultImg
                            }
                        />
                        {!isReady && <EngineeringIcon className={classes.icon} />}
                    </Link>
                )}

                <CardContent>
                    <Typography variant="subtitle2">{project.name}</Typography>
                    <Typography color="primary" variant="subtitle2">
                        {message()}
                    </Typography>
                    <Typography variant="body2">{project.description}</Typography>
                </CardContent>
            </CardActionArea>
        </Card>
    );
};

export default ProjectCard;
